/**
 * CardExamples.tsx
 * Comprehensive examples of HeroUI Card component
 * 
 * Showcases all HeroUI Card features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Card, CardHeader, CardBody, CardFooter } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { 
  Heart, 
  Share, 
  Bookmark, 
  MoreHorizontal,
  TrendingUp,
  Activity,
  DollarSign,
  Users,
  Calendar,
  MapPin,
  Clock,
  Download,
  Settings
} from 'lucide-react';


export const CardExamples: React.FC = () => {
  const [likedCards, setLikedCards] = useState<Record<string, boolean>>({});
  const [bookmarkedCards, setBookmarkedCards] = useState<Record<string, boolean>>({});

  const toggleLike = (cardId: string) => {
    setLikedCards(prev => ({ ...prev, [cardId]: !prev[cardId] }));
  };

  const toggleBookmark = (cardId: string) => {
    setBookmarkedCards(prev => ({ ...prev, [cardId]: !prev[cardId] }));
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Card Variants</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardBody>
                <h4 className="font-semibold mb-2">Surface Card</h4>
                <p className="text-text-secondary">Uses surface background color with border for subtle elevation.</p>
              </CardBody>
            </Card>
            
            <Card className="bg-background">
              <CardBody>
                <h4 className="font-semibold mb-2">Background Card</h4>
                <p className="text-text-secondary">Uses background color for cards that need to blend with the page.</p>
              </CardBody>
            </Card>
            
            <Card shadow="lg" isHoverable>
              <CardBody>
                <h4 className="font-semibold mb-2">Elevated Card</h4>
                <p className="text-text-secondary">Uses shadow and hover effects for prominent content areas.</p>
              </CardBody>
            </Card>
          </div>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Basic Card Structure</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <h4 className="font-semibold">Card with Header</h4>
              </CardHeader>
              <CardBody>
                <p className="text-text-secondary">
                  This card demonstrates the basic structure with a header and body section.
                </p>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <h4 className="font-semibold">Card with Actions</h4>
                  <Button variant="ghost" size="sm" className="w-8 h-8 p-0 min-w-8 flex-shrink-0 ml-4">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <p className="text-text-secondary">
                  Cards can include action buttons in the header area.
                </p>
              </CardBody>
              <CardFooter>
                <div className="flex gap-2">
                  <Button variant="bordered" color="primary" size="sm">
                    <Share className="w-4 h-4" />
                    Share
                  </Button>
                  <Button color="primary" size="sm">
                    Read More
                  </Button>
                </div>
              </CardFooter>
            </Card>
          </div>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Interactive Cards</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card shadow="lg" isHoverable isPressable onPress={() => console.log('Card clicked!')}>
              <CardBody>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Activity className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Pressable Card</h4>
                    <p className="text-sm text-text-secondary">Click me!</p>
                  </div>
                </div>
                <p className="text-text-secondary">
                  This entire card is clickable and shows hover effects.
                </p>
              </CardBody>
            </Card>

            <Card isHoverable>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <h4 className="font-semibold">Hoverable Card</h4>
                  <div className="flex gap-1 flex-shrink-0 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-8 h-8 p-0 min-w-8"
                      onPress={() => toggleLike('hover-card')}
                    >
                      <Heart 
                        className={`w-4 h-4 ${likedCards['hover-card'] ? 'fill-current text-red-500' : 'text-text-secondary'}`} 
                      />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-8 h-8 p-0 min-w-8"
                      onPress={() => toggleBookmark('hover-card')}
                    >
                      <Bookmark 
                        className={`w-4 h-4 ${bookmarkedCards['hover-card'] ? 'fill-current text-yellow-500' : 'text-text-secondary'}`} 
                      />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <p className="text-text-secondary">
                  This card shows hover effects without being clickable.
                </p>
              </CardBody>
            </Card>
          </div>
        </div>


        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Trading Dashboard Cards</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardBody>
                <div className="flex items-center justify-between mb-2">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                  <Chip size="sm" color="success" variant="flat">+2.4%</Chip>
                </div>
                <h4 className="font-semibold text-lg">$12,450</h4>
                <p className="text-sm text-text-secondary">Portfolio Value</p>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <div className="flex items-center justify-between mb-2">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                    <Activity className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <Badge content="15" color="primary" size="sm">
                    <Chip size="sm" color="primary" variant="flat">New</Chip>
                  </Badge>
                </div>
                <h4 className="font-semibold text-lg">89</h4>
                <p className="text-sm text-text-secondary">Active Trades</p>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <div className="flex items-center justify-between mb-2">
                  <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <Chip size="sm" color="warning" variant="flat">Today</Chip>
                </div>
                <h4 className="font-semibold text-lg">$2,340</h4>
                <p className="text-sm text-text-secondary">Daily P&L</p>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <div className="flex items-center justify-between mb-2">
                  <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center">
                    <Users className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                  </div>
                  <Button variant="ghost" size="sm" className="w-6 h-6 p-0 min-w-6">
                    <MoreHorizontal className="w-3 h-3" />
                  </Button>
                </div>
                <h4 className="font-semibold text-lg">76%</h4>
                <p className="text-sm text-text-secondary">Win Rate</p>
              </CardBody>
            </Card>
          </div>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Complex Card Compositions</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card shadow="lg" isHoverable>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold">Market Analysis</h4>
                    <p className="text-sm text-text-secondary">EUR/USD • 4H Chart</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0 ml-4">
                    <Button variant="ghost" size="sm" className="w-8 h-8 p-0 min-w-8">
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="w-8 h-8 p-0 min-w-8">
                      <Settings className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <div className="h-40 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/10 dark:to-indigo-900/20 rounded-lg mb-4 flex items-center justify-center">
                  <div className="text-center">
                    <TrendingUp className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-sm text-text-secondary">Chart Area</p>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-text-secondary">Current Price</p>
                    <p className="font-semibold text-green-600">1.0845 (+0.23%)</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-text-secondary">24h Volume</p>
                    <p className="font-semibold">$45.2B</p>
                  </div>
                </div>
              </CardBody>
              <CardFooter>
                <div className="flex gap-2 w-full">
                  <Button color="success" size="sm" className="flex-1">
                    Buy
                  </Button>
                  <Button color="danger" size="sm" className="flex-1">
                    Sell
                  </Button>
                </div>
              </CardFooter>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <h4 className="font-semibold">Recent Activity</h4>
                  <Button variant="ghost" size="sm" className="flex-shrink-0 ml-4">View All</Button>
                </div>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-900/10">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">Profitable Trade Closed</p>
                      <p className="text-xs text-text-secondary">AAPL • +$234.50</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-text-secondary flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        2h ago
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/10">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <Activity className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">New Position Opened</p>
                      <p className="text-xs text-text-secondary">TSLA • 100 shares</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-text-secondary flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        4h ago
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/10">
                    <div className="w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center">
                      <Calendar className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">Strategy Updated</p>
                      <p className="text-xs text-text-secondary">Momentum Trading Plan</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-text-secondary flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        1d ago
                      </p>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>


        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Nested Cards</h3>
          <Card  className="p-6">
            <div>
              <h4 className="font-semibold text-lg mb-4">Trading Session Overview</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardBody>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <h5 className="font-semibold">Session Stats</h5>
                        <p className="text-sm text-text-secondary">New York Session</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-text-secondary">Trades</p>
                        <p className="font-semibold">12</p>
                      </div>
                      <div>
                        <p className="text-xs text-text-secondary">P&L</p>
                        <p className="font-semibold text-green-600">+$456</p>
                      </div>
                    </div>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                        <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h5 className="font-semibold">Time Analysis</h5>
                        <p className="text-sm text-text-secondary">Active Trading Hours</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-text-secondary">Duration</p>
                        <p className="font-semibold">4h 23m</p>
                      </div>
                      <div>
                        <p className="text-xs text-text-secondary">Avg/Trade</p>
                        <p className="font-semibold">22m</p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </PlaygroundPageLayout>
  );
};